/**
 * The thread's prose, rendered: bold, italics and short lists in what the model
 * says, as React elements — never as an HTML string.
 *
 * WHY. Every Ask answer a usability study collected wore bold headings, and the
 * thread printed them as asterisks. The prompts now ask for plain paragraphs
 * (`engine/chat.ts`, `ui/narrator.ts`), but a prompt is a request, and stored
 * transcripts keep the words they were said in. So the voice's turns and the
 * Ask answers go through this, and what the model marked up reads as it meant.
 *
 * WHAT IT IS NOT. Markdown. A closed set: `**bold**`, `*italics*`, lines that
 * start "- ", "* " or "1. " as a list, and a line that is only "**Heading**" or
 * "## Heading" as a bold line. Everything else — links, code, tables, HTML — is
 * text, printed as the model wrote it. Every string reaches the page as a React
 * text child, which React escapes; there is no HTML string anywhere in this file
 * for anything the model writes to become.
 *
 * STREAMING. The parse runs on whatever has arrived, on every delta. A marker
 * with no partner yet is text: an answer mid-stream shows "**The mech" until
 * the closing pair lands, and then the same words turn bold in place. Nothing is
 * held back, so rendering never makes the stream slower — but for the front
 * half of a mark still arriving, below. And only on a delta to THIS text: the
 * parse is memoised on the words, the graph and the caret, so a finished answer
 * up the thread is not re-read while another one streams, or while a pass's
 * thinking arrives a few characters at a time (D335).
 *
 * MARKS. Where a surface hands in the graph (`refs`), a `[[id]]` the model put
 * after a sentence that rests on a node becomes a press labelled with that
 * node's label from the session, never the model's words, and an id the graph
 * does not hold renders as nothing (`engine/refs.ts`, D295). Without `refs` a
 * mark is text like any other bracket: the pinned candidate threads send the
 * model no ids, so there is nothing for one to name.
 */

import { useMemo, type ReactNode } from 'react'
import { heldBack, splitRefs } from '../engine/refs'

/** A run of inline text: plain, emphasised around more of the same, or a mark
 *  the graph holds. */
export type Span =
  | { kind: 'text'; text: string }
  | { kind: 'strong' | 'em'; children: Span[] }
  | { kind: 'ref'; id: string }

/** One block of prose, in the order it was written. */
export type Block =
  | { kind: 'para'; spans: Span[] }
  | { kind: 'heading'; spans: Span[] }
  | { kind: 'list'; ordered: boolean; start: number; items: Span[][] }

const BLANK = /^\s*$/
const HASH_HEADING = /^\s{0,3}#{1,6}\s+(.*?)\s*#*\s*$/
/** A line that is nothing but one bold run, a colon at most after it. */
const BOLD_HEADING = /^\s*\*\*([^*]+?)\*\*\s*:?\s*$/
const BULLET = /^\s*[-*•]\s+(.*)$/
const NUMBERED = /^\s*(\d{1,3})[.)]\s+(.*)$/
/** An indented line under a list item continues that item. */
const CONTINUES = /^\s{2,}\S/

/**
 * Where an emphasis run opened just before `from` closes, or -1 if its partner
 * has not arrived. The opener must hug the text after it and the closer the text
 * before it — "5 * 3 * 2" is arithmetic, not italics — and a single `*` skips
 * past any `**` pair inside it, so bold within italics does not end the italics.
 */
function closing(text: string, from: number, mark: '*' | '**'): number {
  if (from >= text.length || /\s/.test(text[from]!)) return -1
  for (let j = from + 1; j < text.length; j++) {
    if (!text.startsWith(mark, j)) continue
    if (mark === '*' && text[j + 1] === '*') {
      j++
      continue
    }
    if (/\s/.test(text[j - 1]!)) continue
    return j
  }
  return -1
}

/** Inline emphasis, nested at most two deep — past that it is text. */
export function parseSpans(text: string, depth = 0): Span[] {
  const out: Span[] = []
  let buf = ''
  const flush = () => {
    if (buf) out.push({ kind: 'text', text: buf })
    buf = ''
  }
  let i = 0
  while (i < text.length) {
    if (text[i] !== '*') {
      buf += text[i]
      i++
      continue
    }
    const mark = text[i + 1] === '*' ? '**' : '*'
    const open = i + mark.length
    const close = depth < 2 ? closing(text, open, mark) : -1
    if (close < 0) {
      // No partner (yet): the marker is text, and so is everything after it
      // until one arrives.
      buf += mark
      i = open
      continue
    }
    flush()
    out.push({
      kind: mark === '**' ? 'strong' : 'em',
      children: parseSpans(text.slice(open, close), depth + 1),
    })
    i = close + mark.length
  }
  flush()
  return out
}

/**
 * The blocks, line by line. A blank line ends whatever was open; a list runs
 * while its lines keep their marker (or are indented under one); a heading is a
 * whole line or it is not a heading. Lines inside a paragraph keep their newline,
 * which HTML shows as a space — what the thread always did with them.
 */
export function parseProse(text: string): Block[] {
  const blocks: Block[] = []
  let para: string[] = []
  let list: { ordered: boolean; start: number; items: string[] } | null = null

  const endPara = () => {
    if (para.length) blocks.push({ kind: 'para', spans: parseSpans(para.join('\n')) })
    para = []
  }
  const endList = () => {
    if (list)
      blocks.push({
        kind: 'list',
        ordered: list.ordered,
        start: list.start,
        items: list.items.map((item) => parseSpans(item)),
      })
    list = null
  }

  for (const line of text.split('\n')) {
    if (BLANK.test(line)) {
      endPara()
      endList()
      continue
    }
    const heading = HASH_HEADING.exec(line) ?? BOLD_HEADING.exec(line)
    if (heading && heading[1]!.trim()) {
      endPara()
      endList()
      blocks.push({ kind: 'heading', spans: parseSpans(heading[1]!.replace(/\*\*/g, '')) })
      continue
    }
    const numbered = NUMBERED.exec(line)
    const bullet = numbered ? null : BULLET.exec(line)
    if (numbered || bullet) {
      endPara()
      const ordered = numbered !== null
      if (list && list.ordered !== ordered) endList()
      list ??= { ordered, start: numbered ? Number(numbered[1]) : 1, items: [] }
      list.items.push(numbered ? numbered[2]! : bullet![1]!)
      continue
    }
    if (list && CONTINUES.test(line)) {
      list.items[list.items.length - 1] += `\n${line.trim()}`
      continue
    }
    endList()
    para.push(line)
  }
  endPara()
  endList()
  return blocks
}

/** The graph a surface lets marks name, and what a press on one does. */
export interface ProseRefs {
  nodes: Readonly<Record<string, { id: string; label: string; claim: string } | undefined>>
  onPivot: (id: string) => void
  /** The pointer or focus reached a mark, and left it — the proposal chips'
   *  hover ring (`ui/proposalMark.ts`), so a mark says where it points. */
  onPoint?: (id: string) => void
  onRelease?: (id: string) => void
}

/** Every text run with its marks split out, each checked against the graph. */
function withRefs(spans: Span[], nodes: ProseRefs['nodes']): Span[] {
  return spans.flatMap((s): Span[] =>
    s.kind === 'text'
      ? splitRefs(s.text, nodes).map((p) =>
          'ref' in p ? { kind: 'ref', id: p.ref } : { kind: 'text', text: p.text },
        )
      : s.kind === 'ref'
        ? [s]
        : [{ ...s, children: withRefs(s.children, nodes) }],
  )
}

/** A block's runs, with its marks split out. */
function blockWithRefs(b: Block, nodes: ProseRefs['nodes']): Block {
  return b.kind === 'list'
    ? { ...b, items: b.items.map((item) => withRefs(item, nodes)) }
    : { ...b, spans: withRefs(b.spans, nodes) }
}

/**
 * Blocks, with the marks a surface's graph holds split out as presses. A live
 * answer is cut first before the front half of a mark still arriving, so its
 * machine text never flashes for a frame (`heldBack`); a finished one keeps an
 * unclosed pair as the model's text.
 */
export function parseProseRefs(text: string, nodes: ProseRefs['nodes'], live = false): Block[] {
  return parseProse(live ? heldBack(text) : text).map((b) => blockWithRefs(b, nodes))
}

function spansOut(spans: Span[], refs?: ProseRefs): ReactNode[] {
  return spans.map((s, i) => {
    if (s.kind === 'text') return s.text
    if (s.kind !== 'ref') {
      const inner = spansOut(s.children, refs)
      return s.kind === 'strong' ? <strong key={i}>{inner}</strong> : <em key={i}>{inner}</em>
    }
    // Only `parseProseRefs` makes a mark, and only for a node the graph held;
    // one pruned since this render's graph was read renders as nothing.
    const node = refs?.nodes[s.id]
    if (!refs || !node) return null
    // Untinted, whatever the node's standing: nothing on this channel passed a
    // gate, and a node's mark colour inside an unchecked answer would read as
    // the answer borrowing it. The press is the check.
    return (
      <button
        key={i}
        type="button"
        className="ask-ref"
        title={node.claim}
        onClick={() => refs.onPivot(node.id)}
        onMouseEnter={() => refs.onPoint?.(node.id)}
        onMouseLeave={() => refs.onRelease?.(node.id)}
        onFocus={() => refs.onPoint?.(node.id)}
        onBlur={() => refs.onRelease?.(node.id)}
      >
        {node.label}
      </button>
    )
  })
}

/**
 * Model prose, rendered. `live` puts the streaming caret after the last word,
 * wherever the last word is — the end of a paragraph, a heading, or a list's
 * last item — and gives an answer with nothing in it yet a caret of its own.
 */
export function Prose({
  text,
  live = false,
  className = 'ask-a',
  refs,
}: {
  text: string
  live?: boolean
  className?: string
  /** The graph marks may name. Absent, a mark is text (see MARKS above). */
  refs?: ProseRefs
}) {
  // Keyed on `refs.nodes` and not on `refs`: a surface builds the refs object
  // afresh on every render, while the graph it carries changes only when a node
  // does. The presses read `refs` below, at render, so they are never stale.
  const nodes = refs?.nodes
  const blocks = useMemo(
    () => (nodes ? parseProseRefs(text, nodes, live) : parseProse(text)),
    [text, nodes, live],
  )
  const caret = live ? <i className="caret" /> : null
  if (blocks.length === 0) return caret ? <p className={className}>{caret}</p> : null
  return (
    <>
      {blocks.map((b, i) => {
        const tail = i === blocks.length - 1 ? caret : null
        if (b.kind === 'para')
          return (
            <p key={i} className={className}>
              {spansOut(b.spans, refs)}
              {tail}
            </p>
          )
        if (b.kind === 'heading')
          return (
            <p key={i} className={`${className} prose-heading`}>
              <strong>{spansOut(b.spans, refs)}</strong>
              {tail}
            </p>
          )
        const items = b.items.map((item, j) => (
          <li key={j}>
            {spansOut(item, refs)}
            {j === b.items.length - 1 ? tail : null}
          </li>
        ))
        return b.ordered ? (
          <ol
            key={i}
            className={`${className} prose-list`}
            {...(b.start !== 1 ? { start: b.start } : {})}
          >
            {items}
          </ol>
        ) : (
          <ul key={i} className={`${className} prose-list`}>
            {items}
          </ul>
        )
      })}
    </>
  )
}
